<template>
    <div class="email-container">
        <div class="input-container">
            <ds-input-field
                v-model.trim="email"
                :autocomplete="autocomplete"
                :label="label"
                :submitted="isSubmitted"
                :invalid="isInvalid"
                :required="isRequired"
                :class="cssClass"
                type="email"
                @input="handleEmailInput"
                @blur="handleEmailBlur"
            >
                <template #error>
                    <slot name="errors">
                        <div>
                            {{ errorText }}
                        </div>
                    </slot>
                </template>
            </ds-input-field>
            <slot name="trailing" />
        </div>
        <slot name="rrsWarning">
            <ds-inline-alert
                v-if="areRRSMandatoryChecksValid && !areRRSOtherListsValid"
                class="email-valid-check"
                leading-icon
                :type="loadingRRSCheck ? 'subtle': 'warning'"
            >
                <p v-if="!loadingRRSCheck" class="validity-description">
                    {{ $t('components.contact.email.RecipientRiskEmailInput.invalidStatus') }}

                    <b>
                        {{ $t('components.contact.email.RecipientRiskEmailInput.systemOptOut') }}
                    </b>
                </p>
                <div v-else class="checking-status">
                    <ds-spinner :size="25" class="status-spinner" />
                    <p class="validity-description">
                        {{ $t('components.contact.email.RecipientRiskEmailInput.checkingEmailStatus') }}
                    </p>
                </div>
                <template v-if="!loadingRRSCheck" #trailing>
                    <ds-filled-button
                        class="more-info-button"
                        gray
                        dense
                        @click="goToEmailStatusPage"
                    >
                        {{ $t('components.contact.email.RecipientRiskEmailInput.moreInfo') }}
                    </ds-filled-button>
                </template>
            </ds-inline-alert>
        </slot>
    </div>
</template>


<script>
import { patterns } from '@infusionsoft/vue-utils';
import debounce from 'lodash.debounce';
import { getEmailValidityStatuses, EMAIL_RRS_INVALID_REASONS } from '@/api/contact/email/emailStatus';
import { INPUT_DEBOUNCE_DELAY } from '@/components/shared/constants/timing.constants';

export default {
    props: {
        emailAddress: {
            default: '',
            type: String,
        },
        submitted: {
            default: false,
            type: Boolean,
        },
        isRequired: {
            default: false,
            type: Boolean,
        },
        emailStatus: {
            default: () => {},
            type: Object,
        },
        validateWithRegex: {
            default: true,
            type: Boolean,
        },
        validateWithRRS: {
            default: true,
            type: Boolean,
        },
        rrsPath: {
            default: '/app/risk/recipientRisk/scanRecipients',
            type: String,
        },
        label: {
            default: '',
            type: String,
        },
        cssClass: {
            default: '',
            type: String,
        },
        autocomplete: {
            default: 'autocomplete',
            type: String,
        },
        ignoreReasons: {
            default: () => ['SPAMTRAP_EMAILS', 'SPAMTRAP_DOMAINS', 'DEACTIVATED_EMAILS', 'SUPPRESSED_EMAILS'],
            type: Array,
        },
    },

    data() {
        return {
            email: '',
            loadingRRSCheck: false,
            currentStatus: {},
        };
    },

    mounted() {
        this.email = this.emailAddress;
        this.currentStatus = this.emailStatus;
    },

    created() {
        this.handleEmailInput = debounce(this.handleEmailInput, INPUT_DEBOUNCE_DELAY);
    },

    watch: {
        emailStatus(status) {
            this.currentStatus = status;
        },
    },

    computed: {
        isInvalid() {
            return !this.isRegexValid || !this.areRRSMandatoryChecksValid;
        },

        isSubmitted() {
            return this.isInvalid || this.submitted;
        },

        isRegexValid() {
            if (this.email && this.validateWithRegex) {
                return RegExp(patterns.email).test(this.email);
            }

            return true;
        },

        areRRSMandatoryChecksValid() {
            if (this.email && this.validateWithRRS && this.currentStatus && Object.keys(this.currentStatus).length > 0) {
                return this.everyReasonPassesRRSMandatoryChecks(this.currentStatus.reasonList);
            }

            return true;
        },

        areRRSOtherListsValid() {
            if (this.email && this.validateWithRRS && this.currentStatus && Object.keys(this.currentStatus).length > 0) {
                return this.currentStatus.reasonList == null || this.currentStatus.reasonList.length === 0 || this.everyReasonIsOnIgnoreList(this.currentStatus.reasonList);
            }

            return true;
        },

        errorText() {
            if (this.isInvalid) {
                if (!this.isRegexValid) {
                    return this.$t('components.contact.email.RecipientRiskEmailInput.invalidEmail');
                }

                if (!this.areRRSMandatoryChecksValid) {
                    const invalidReason = this.currentStatus.reasonList.filter((reason) => {
                        return EMAIL_RRS_INVALID_REASONS.includes(reason.reasonClass);
                    });

                    if (invalidReason?.length) { return invalidReason[0].reason; }
                }
            }

            return '';
        },
    },

    methods: {

        async handleEmailInput() {
            this.currentStatus = {};

            if (this.email && this.isRegexValid) {
                if (this.validateWithRRS) {
                    await this.loadEmailStatus();
                }
            }

            this.$emit('input', this);
        },

        handleEmailBlur() {
            this.$emit('blur', this);
        },

        async loadEmailStatus() {
            this.loadingRRSCheck = true;
            const emails = [this.email];

            try {
                const recipientRiskScanBulk = await getEmailValidityStatuses(this.rrsPath, emails);

                if (recipientRiskScanBulk?.recipients?.length) {
                    [this.currentStatus] = recipientRiskScanBulk.recipients;
                }
            } catch (e) {
                return;
            } finally {
                this.loadingRRSCheck = false;
            }
        },

        everyReasonIsOnIgnoreList(reasonList) {
            if (reasonList?.length) {
                return reasonList.every((reason) => this.ignoreReasons.includes(reason.reasonClass) && !EMAIL_RRS_INVALID_REASONS.includes(reason.reasonClass));
            }

            return false;
        },

        everyReasonPassesRRSMandatoryChecks(reasonList) {
            if (reasonList?.length) {
                return !reasonList.some((reason) => EMAIL_RRS_INVALID_REASONS.includes(reason.reasonClass));
            }

            return true;
        },

        goToEmailStatusPage() {
            window.open('https://learn.thryv.com/hc/en-us/articles/37278655470221-Email-statuses-explained');
        },
    },
};
</script>

<style lang="scss" scoped>
    .input-container {
        --input-field-margin-top: #{$spacing-100};
        --input-margin-bottom: #{$spacing-100};
        display: flex;
        width:100%;
    }

    .email-container {
        display: block;
        width:100%;
    }

    .email-valid-check {
        width: 100%;
    }

    .more-info-button {
        white-space: nowrap;
    }

    .validity-description {
        margin-bottom: 0;
    }

    .checking-status {
        display: flex;
    }
</style>
